/**
 * Color Utilities — parsing and opacity manipulation.
 *
 * Supports hex (#RGB, #RRGGBB) and rgb(r, g, b) formats.
 * Used by the brand token derivation system to compute accent-derived tokens.
 */

export interface RGB {
  r: number;
  g: number;
  b: number;
}

const HEX_6_RE = /^#([0-9a-f]{6})$/i;
const HEX_3_RE = /^#([0-9a-f]{3})$/i;
const RGB_RE = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/;

/**
 * Parses a color string (hex #RRGGBB, #RGB, or rgb(r,g,b)) into {r, g, b}.
 * Returns null for invalid color strings.
 *
 * Handles: uppercase hex, extra whitespace in rgb(), shorthand #RGB.
 * Rejects: rgba(), hsl(), named colors, hex without #, out-of-range values.
 */
export function parseColor(color: string): RGB | null {
  if (!color || typeof color !== 'string') return null;

  const trimmed = color.trim();

  // Try #RRGGBB
  const hex6Match = trimmed.match(HEX_6_RE);
  if (hex6Match) {
    const hex = hex6Match[1];
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }

  // Try #RGB (shorthand)
  const hex3Match = trimmed.match(HEX_3_RE);
  if (hex3Match) {
    const hex = hex3Match[1];
    return {
      r: parseInt(hex[0] + hex[0], 16),
      g: parseInt(hex[1] + hex[1], 16),
      b: parseInt(hex[2] + hex[2], 16),
    };
  }

  // Try rgb(r, g, b)
  const rgbMatch = trimmed.match(RGB_RE);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);

    // Validate range [0, 255]
    if (r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
      return null;
    }

    return { r, g, b };
  }

  return null;
}

/**
 * Takes an rgb/hex color string and returns it at the specified opacity as rgba.
 * Returns empty string if the color cannot be parsed.
 *
 * @example withOpacity('rgb(56, 189, 248)', 0.15) → 'rgba(56, 189, 248, 0.15)'
 * @example withOpacity('#38bdf8', 0.3) → 'rgba(56, 189, 248, 0.3)'
 */
export function withOpacity(color: string, alpha: number): string {
  const parsed = parseColor(color);
  if (!parsed) return '';
  return `rgba(${parsed.r}, ${parsed.g}, ${parsed.b}, ${alpha})`;
}
